import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { getSuggestionKey, hasOverlappingBills, isAwaitingSpikeReview, mapAllocatedTable, mapSuggestionTable } from 'src/components/allocations/AllocationSuggestion';
import { formatServicePeriod, getServiceTotal, getSpanningServiceDates } from './billDetailsUtils';
import { getAllocationSetting, round2, toNumber } from './billEditUtils';
import { isStillScheduled } from '../monthKeys';

/**
 * The services of one property block, gathered by the property utility they resolve to (LB-6015). A
 * bill can print two charges that the property keeps as one utility — water and sewer on a
 * "Water/Sewer" utility. The allocation service keeps one bill per property utility, so two tables for
 * it would send both and lose the second; together they are one table, charged as their sum.
 *
 * A service not yet tied to a property utility keeps a table of its own: until it is, nothing says it
 * is the same one, and its table is where the property utility gets picked. Each set sits where its
 * first service is listed.
 */
function groupByPropertyUtility(services) {
  const sets = [];
  const byTarget = new Map();
  services.forEach(item => {
    var _item$target, _item$target2;
    const targetKey = getSuggestionKey((_item$target = item.target) === null || _item$target === void 0 ? void 0 : _item$target.propertyId, (_item$target2 = item.target) === null || _item$target2 === void 0 ? void 0 : _item$target2.utilityId);
    const set = targetKey ? byTarget.get(targetKey) : undefined;
    if (set) {
      set.push(item);
      return;
    }
    const created = [item];
    if (targetKey) byTarget.set(targetKey, created);
    sets.push(created);
  });
  return sets;
}

/**
 * What the services on a table add up to, or null when any of them was read without an amount. A sum
 * missing a part would be allocated as if it were the whole charge, so the table waits for an amount to
 * be typed instead, the way a single service read without one always has.
 */
function sumParts(parts) {
  const amounts = parts.map(part => part.amount);
  if (!amounts.length || amounts.some(amount => amount === null || amount === undefined)) return null;

  // To the cent: the sum is shown and sent, and adding floats leaves artefacts (10.1 + 20.2).
  return amounts.length === 1 ? amounts[0] : round2(amounts.reduce((sum, amount) => sum + amount, 0));
}

/**
 * One table, from the services charged to its property utility. It is edited as its first service:
 * the settings are stored by property block and service type, and that is the one they are read and
 * written under, so a typed amount replaces the whole sum and typed dates cover every service on it.
 */
function buildAllocationTable(_ref) {
  var _setting$serviceStart, _setting$serviceEndDa, _goodwillDeductions$g, _toNumber;
  let data = _ref.data,
    goodwillDeductions = _ref.goodwillDeductions,
    propertyIndex = _ref.propertyIndex,
    services = _ref.services,
    settings = _ref.settings;
  const _services = _slicedToArray(services, 1),
    _services$ = _services[0],
    key = _services$.key,
    service = _services$.service,
    target = _services$.target;
  const setting = getAllocationSetting(settings, propertyIndex, service.serviceType);
  const extracted = getSpanningServiceDates(services.map(item => item.service), data);
  const parts = services.map(item => ({
    amount: getServiceTotal(item.service),
    serviceType: item.service.serviceType
  }));
  const extractedAmount = sumParts(parts);
  const serviceStartDate = (_setting$serviceStart = setting.serviceStartDate) !== null && _setting$serviceStart !== void 0 ? _setting$serviceStart : extracted.start;
  const serviceEndDate = (_setting$serviceEndDa = setting.serviceEndDate) !== null && _setting$serviceEndDa !== void 0 ? _setting$serviceEndDa : extracted.end;
  // An emptied amount field falls back to the extraction rather than allocating nothing.
  const overriddenAmount = toNumber(setting.billAmount);
  const billAmount = overriddenAmount === null ? extractedAmount : overriddenAmount;
  const configuredGwd = (_goodwillDeductions$g = goodwillDeductions === null || goodwillDeductions === void 0 ? void 0 : goodwillDeductions[getSuggestionKey(target.propertyId, target.utilityId)]) !== null && _goodwillDeductions$g !== void 0 ? _goodwillDeductions$g : null;
  return _objectSpread(_objectSpread({}, target), {}, {
    billAmount,
    extractedAmount,
    extractedPeriod: formatServicePeriod(extracted.start, extracted.end),
    // Untouched, what the property utility is configured to deduct is what is allocated.
    gwdOverride: (_toNumber = toNumber(setting.gwdPercent)) !== null && _toNumber !== void 0 ? _toNumber : configuredGwd,
    // Shown until the service answers with the deduction it actually applied.
    gwdPercent: configuredGwd !== null && configuredGwd !== void 0 ? configuredGwd : 0,
    key,
    // What the bill charged for each service on the table, so a table of several can say what it adds up.
    parts,
    propertyIndex,
    serviceEndDate,
    servicePeriod: formatServicePeriod(serviceStartDate, serviceEndDate),
    serviceStartDate,
    serviceType: service.serviceType,
    serviceTypes: [...new Set(parts.map(part => part.serviceType).filter(Boolean))],
    setting
  });
}

/**
 * One allocation table per property and utility the bill is allocated for, in the order the
 * extraction lists them. Only the inputs are worked out here — the service dates, the amount, the
 * deduction and which property utility they belong to; the charges themselves come from the
 * allocation service. `goodwillDeductions` are what the properties have configured, keyed by
 * property and utility.
 */
export function buildAllocationTables(_ref2) {
  let data = _ref2.data,
    goodwillDeductions = _ref2.goodwillDeductions,
    groups = _ref2.groups,
    settings = _ref2.settings;
  // Gathered across the whole bill, not one address at a time: two address blocks can resolve to the
  // same property utility (one property, two buildings), and that is still one table. Each table shows
  // under the address its first service is listed at, and is edited under that block's settings.
  const tables = groupByPropertyUtility(groups.flatMap(group => group.services.map(item => _objectSpread(_objectSpread({}, item), {}, {
    propertyIndex: group.propertyIndex
  }))));
  return groups.map(group => ({
    address: group.address,
    propertyIndex: group.propertyIndex,
    utilities: tables.filter(services => services[0].propertyIndex === group.propertyIndex).map(services => buildAllocationTable({
      data,
      goodwillDeductions,
      propertyIndex: group.propertyIndex,
      services,
      settings
    }))
  }));
}

/** The allocation states by property and utility, which is how the tables are keyed. */
export function indexAllocationStates(services) {
  return (services || []).reduce((acc, service) => {
    const key = getSuggestionKey(service === null || service === void 0 ? void 0 : service.propertyId, service === null || service === void 0 ? void 0 : service.utilityId);
    if (key) acc[key] = service;
    return acc;
  }, {});
}

/**
 * Whether there is a saved allocation table to read for this service. Only from the point the
 * allocation service has worked one out — before that the suggestion is all there is to show.
 */
export function hasAllocationTable(service) {
  return !!(service !== null && service !== void 0 && service.billingCycleId) && [BILL_ALLOCATION_STATE.allocated, BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview, BILL_ALLOCATION_STATE.skipped].includes(service.state);
}

/**
 * Whether the allocation service is holding this service back until the bill has been compared with
 * the cycles before it, which is when its comparison is worth reading.
 */
export function isHeldForReview(service) {
  return !!(service !== null && service !== void 0 && service.billingCycleId) && (service === null || service === void 0 ? void 0 : service.state) === BILL_ALLOCATION_STATE.needsReview;
}

/**
 * Folds into the tables what the allocation service says about them: the saved allocation table for
 * a service that has been allocated, and the suggestion for one that has not. Both arrive in the
 * same shape, so a table reads the same either way — what changes is that the allocated one is what
 * residents will actually be charged. Tables still waiting on an answer keep their inputs and show
 * unknown totals rather than zeros.
 *
 * `areStatesRead` is false until the bill's allocation states have come back. The suggestion often arrives first, and
 * the bill it overlaps can be the service's own allocation (LB-6052), which only the states can tell: until they are
 * in, a table is still being calculated, not already billed.
 */
export function withAllocations(groups, _ref3) {
  let allocatedTables = _ref3.allocatedTables,
    allocationStates = _ref3.allocationStates,
    _ref3$areStatesRead = _ref3.areStatesRead,
    areStatesRead = _ref3$areStatesRead === void 0 ? true : _ref3$areStatesRead,
    comparisons = _ref3.comparisons,
    getResidentName = _ref3.getResidentName,
    hasFailed = _ref3.hasFailed,
    isLoading = _ref3.isLoading,
    _ref3$monthInProgress = _ref3.monthInProgress,
    monthInProgress = _ref3$monthInProgress === void 0 ? null : _ref3$monthInProgress,
    reviewingKeys = _ref3.reviewingKeys,
    suggestions = _ref3.suggestions;
  return groups.map(group => _objectSpread(_objectSpread({}, group), {}, {
    utilities: group.utilities.map(table => {
      var _allocationStates$key, _allocation$state, _mapped$overlappingBi, _saved$bill$amount, _saved$bill, _saved$bill$endDateOn, _saved$bill2, _saved$bill$startDate, _saved$bill3, _allocation$error, _allocation$scheduleR, _mapped$allocationMet, _mapped$cadPercent, _comparisons$key, _mapped$deductionPerc, _ref4, _mapped$gwdPercent, _mapped$recoveryPerce, _mapped$skipReason, _mapped$totalBilling, _mapped$units;
      const key = getSuggestionKey(table.propertyId, table.utilityId);
      const allocation = (_allocationStates$key = allocationStates === null || allocationStates === void 0 ? void 0 : allocationStates[key]) !== null && _allocationStates$key !== void 0 ? _allocationStates$key : null;
      const state = (_allocation$state = allocation === null || allocation === void 0 ? void 0 : allocation.state) !== null && _allocation$state !== void 0 ? _allocation$state : BILL_ALLOCATION_STATE.notAllocated;

      // The saved table wins: once a service is allocated, suggesting something else would be
      // showing figures nobody will be charged.
      const allocated = mapAllocatedTable(allocatedTables === null || allocatedTables === void 0 ? void 0 : allocatedTables[key], getResidentName);
      const mapped = allocated !== null && allocated !== void 0 ? allocated : mapSuggestionTable(suggestions === null || suggestions === void 0 ? void 0 : suggestions[key], getResidentName);
      // The bill an allocated service overlaps is the one it was allocated as. Said of a service
      // still to be allocated it is a warning; said of this one it is the service's own shadow.
      const isAllocatedState = state === BILL_ALLOCATION_STATE.allocated;
      // Nor is a service awaiting approval (or its spike review) billed twice by its own allocation, which the suggestion
      // shown until its saved table is read was worked out against (LB-6052): any other bill over the same days still is.
      const isWaitingOnItsOwnAllocation = [BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview].includes(state);
      const overlappingBills = isAllocatedState || !areStatesRead ? [] : ((_mapped$overlappingBi = mapped === null || mapped === void 0 ? void 0 : mapped.overlappingBills) !== null && _mapped$overlappingBi !== void 0 ? _mapped$overlappingBi : []).filter(bill => !isWaitingOnItsOwnAllocation || bill.billId !== (allocation === null || allocation === void 0 ? void 0 : allocation.allocationBillId));
      const isWaitingForMonth = !isAllocatedState && isStillScheduled(allocation === null || allocation === void 0 ? void 0 : allocation.scheduledFor, monthInProgress);
      // What the saved table actually holds. A bill allocated before its services were added up (LB-6015)
      // can hold one service's amount rather than the sum it reads as: shown as it is, and said, so the
      // table never reads as fully billed when it was not.
      const saved = allocatedTables === null || allocatedTables === void 0 ? void 0 : allocatedTables[key];
      const allocatedAmount = allocated ? toNumber((_saved$bill$amount = saved === null || saved === void 0 || (_saved$bill = saved.bill) === null || _saved$bill === void 0 ? void 0 : _saved$bill.amount) !== null && _saved$bill$amount !== void 0 ? _saved$bill$amount : saved === null || saved === void 0 ? void 0 : saved.billTotal) : null;
      const isShortOfBill = (table.parts || []).length > 1 && allocatedAmount !== null && table.extractedAmount !== null && allocatedAmount < table.extractedAmount - 0.005;
      return _objectSpread(_objectSpread({}, table), {}, {
        allocatedAmount,
        isShortOfBill,
        // The period the saved allocation charges, which Apply Billing can move off the bill's own (LB-6030).
        savedServiceEndDate: (_saved$bill$endDateOn = saved === null || saved === void 0 || (_saved$bill2 = saved.bill) === null || _saved$bill2 === void 0 ? void 0 : _saved$bill2.endDateOnly) !== null && _saved$bill$endDateOn !== void 0 ? _saved$bill$endDateOn : null,
        savedServiceStartDate: (_saved$bill$startDate = saved === null || saved === void 0 || (_saved$bill3 = saved.bill) === null || _saved$bill3 === void 0 ? void 0 : _saved$bill3.startDateOnly) !== null && _saved$bill$startDate !== void 0 ? _saved$bill$startDate : null,
        allocationError: (_allocation$error = allocation === null || allocation === void 0 ? void 0 : allocation.error) !== null && _allocation$error !== void 0 ? _allocation$error : null,
        // Scheduled for a later month (LB-6009): Allocate takes it once that month opens. Once the month in progress has
        // reached it, it waits for nothing and is allocated like any other.
        scheduledFor: isWaitingForMonth ? allocation.scheduledFor : null,
        scheduleReason: isWaitingForMonth ? (_allocation$scheduleR = allocation.scheduleReason) !== null && _allocation$scheduleR !== void 0 ? _allocation$scheduleR : null : null,
        allocationMethod: (_mapped$allocationMet = mapped === null || mapped === void 0 ? void 0 : mapped.allocationMethod) !== null && _mapped$allocationMet !== void 0 ? _mapped$allocationMet : null,
        allocationState: state,
        cadPercent: (_mapped$cadPercent = mapped === null || mapped === void 0 ? void 0 : mapped.cadPercent) !== null && _mapped$cadPercent !== void 0 ? _mapped$cadPercent : 0,
        comparison: (_comparisons$key = comparisons === null || comparisons === void 0 ? void 0 : comparisons[key]) !== null && _comparisons$key !== void 0 ? _comparisons$key : null,
        deductionPercent: (_mapped$deductionPerc = mapped === null || mapped === void 0 ? void 0 : mapped.deductionPercent) !== null && _mapped$deductionPerc !== void 0 ? _mapped$deductionPerc : null,
        gwdPercent: (_ref4 = (_mapped$gwdPercent = mapped === null || mapped === void 0 ? void 0 : mapped.gwdPercent) !== null && _mapped$gwdPercent !== void 0 ? _mapped$gwdPercent : table.gwdPercent) !== null && _ref4 !== void 0 ? _ref4 : 0,
        hasFailed: !!hasFailed && !mapped,
        isAllocated: !!allocated,
        isApproved: isAllocatedState,
        // Its overlaps can't be judged yet, so nothing may be allocated or approved from it until they can.
        isAwaitingStates: !areStatesRead,
        isComputable: mapped ? mapped.isComputable : true,
        // Approved in Apply Billing, where it was made, never from this page (LB-5952).
        isMadeInApplyBilling: !!(allocation !== null && allocation !== void 0 && allocation.isMadeInApplyBilling),
        // An allocated table is worked out and saved; nothing is being waited on for it.
        isLoading: !allocated && (!!isLoading || !areStatesRead),
        isReviewing: (reviewingKeys || []).includes(key),
        overlappingBills,
        recoveryPercent: (_mapped$recoveryPerce = mapped === null || mapped === void 0 ? void 0 : mapped.recoveryPercent) !== null && _mapped$recoveryPerce !== void 0 ? _mapped$recoveryPerce : null,
        skipReason: (_mapped$skipReason = mapped === null || mapped === void 0 ? void 0 : mapped.skipReason) !== null && _mapped$skipReason !== void 0 ? _mapped$skipReason : null,
        totalBilling: (_mapped$totalBilling = mapped === null || mapped === void 0 ? void 0 : mapped.totalBilling) !== null && _mapped$totalBilling !== void 0 ? _mapped$totalBilling : null,
        units: (_mapped$units = mapped === null || mapped === void 0 ? void 0 : mapped.units) !== null && _mapped$units !== void 0 ? _mapped$units : []
      });
    })
  }));
}

/**
 * A table can only be approved once it holds charges to approve, and not if the property has already
 * been billed for these days on this utility — the same reason the bill would be refused on save. Nor
 * before the bill's allocation states are in, without which that can't be told (LB-6052).
 */
export function isApprovable(table) {
  return !table.needsProperty && !table.needsUtility && !table.isAwaitingStates && table.isComputable && !hasOverlappingBills(table) && (table.units || []).length > 0;
}

/**
 * Whether pressing Allocate on this table would do anything: nobody has allocated it, or the last try
 * was refused. A service already allocated is not this button's to move — once worked out, it waits
 * for Approve (LB-6052).
 */
export function isPendingAllocation(table) {
  var _table$allocationStat;
  const state = (_table$allocationStat = table.allocationState) !== null && _table$allocationStat !== void 0 ? _table$allocationStat : BILL_ALLOCATION_STATE.notAllocated;
  return isApprovable(table) && [BILL_ALLOCATION_STATE.notAllocated, BILL_ALLOCATION_STATE.failed].includes(state);
}

/**
 * Whether the table is worked out and waits for somebody to approve it, the step that charges the
 * residents on it (LB-6052). One held back for review joins it once its comparison has been
 * reviewed. Not one allocated in Apply Billing: it is approved there, and this page's approve would
 * leave it alone (LB-5952).
 */
export function isAwaitingApproval(table) {
  if (!isApprovable(table) || table.isMadeInApplyBilling) return false;
  if (table.allocationState === BILL_ALLOCATION_STATE.needsReview) return !isAwaitingSpikeReview(table);
  return table.allocationState === BILL_ALLOCATION_STATE.awaitingApproval;
}

/**
 * What the approve request names for these tables: each one's property and utility, once. The
 * service keeps one table per property utility (LB-6015), so naming one twice would not approve
 * anything more.
 */
export function buildApproveItems(tables) {
  const seen = new Set();
  return tables.reduce((items, table) => {
    const key = getSuggestionKey(table.propertyId, table.utilityId);
    if (!key || seen.has(key)) return items;
    seen.add(key);
    items.push({
      propertyId: table.propertyId,
      utilityId: table.utilityId
    });
    return items;
  }, []);
}